/**
 * @voltxampere/components — TRIAC + Optocoupler-TRIAC palette entries
 * (Sprint 79.3 V15, ADR-0116).
 *
 * Sprint 56 ADR-0087 shipped the engine stamps:
 *   - packages/engine/src/stamps/nonlinear/triac.ts
 *   - packages/engine/src/stamps/nonlinear/optocoupler-triac.ts
 *
 * The component palette never surfaced matching definitions, so
 * users could not place either part from the canvas-side palette.
 * Sprint 79.3 V15 audit (discoverability 68/100) flagged this as
 * the only P1 from the V15 organization vector.
 *
 * Sprint 79.3 ships the palette entries with parameter schemas
 * mirroring the engine defaults at `triac.ts` lines 70-117.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const TriacParamsSchema = z
    .object({
    /** Off-state conductance (S). Sprint 56 default 1e-9. */
    gOff: z.number().positive().finite().default(1e-9),
    /** On-state resistance (Ω). Sprint 56 default 0.5. */
    rOn: z.number().positive().finite().default(0.5),
    /** On-state voltage drop (V). Sprint 56 default 1.5. */
    vOn: z.number().positive().finite().default(1.5),
    /** Gate trigger voltage (V). Sprint 56 default 1.0. */
    vGateTrigger: z.number().positive().finite().default(1.0),
    /** Hold-voltage proxy (V). Sprint 56 default 1.0. */
    vHoldProxy: z.number().positive().finite().default(1.0),
    /** Unidirectional (SCR-like) mode flag. Sprint 56 default false. */
    unidirectional: z.boolean().default(false),
    /** Gate-turn-off support (GTO variant). Sprint 56 default false. */
    gateTurnOff: z.boolean().default(false),
    /** Gate turn-off voltage threshold (V). Sprint 56 default 1.5. */
    vGateTurnOff: z.number().positive().finite().default(1.5),
})
    .strict();
export const triacDefinition = {
    type: "triac",
    category: "active",
    displayName: { tr: "TRIAC", en: "TRIAC" },
    symbol: "Q",
    pins: [
        { id: "mt1", side: "left", offset: 0, label: "MT1" },
        { id: "mt2", side: "right", offset: 0, label: "MT2" },
        { id: "gate", side: "bottom", offset: 0, label: "G" },
    ],
    parameterSchema: TriacParamsSchema,
    nonlinear: true,
};
// Optocoupler-TRIAC composite — packaged TRIAC driven by an LED.
// Engine stamp at packages/engine/src/stamps/nonlinear/optocoupler-triac.ts
// expects: led-side anode/cathode + triac-side mt1/mt2 + iLedTrigger.
export const OptocouplerTriacParamsSchema = z
    .object({
    /** LED forward trigger current (A). MOC3041 datasheet ≈ 5 mA. */
    iLedTrigger: z.number().positive().finite().default(5e-3),
    /** TRIAC off-state conductance (S). */
    gOff: z.number().positive().finite().default(1e-9),
    /** TRIAC on-state resistance (Ω). */
    rOn: z.number().positive().finite().default(0.5),
    /** TRIAC on-state voltage drop (V). */
    vOn: z.number().positive().finite().default(1.5),
    /** TRIAC hold-voltage proxy (V). */
    vHoldProxy: z.number().positive().finite().default(1.0),
    /** LED Shockley Is (A). */
    ledIs: z.number().positive().finite().default(1e-12),
    /** LED Shockley ideality factor. */
    ledN: z.number().positive().finite().default(3),
})
    .strict();
export const optocouplerTriacDefinition = {
    type: "optocoupler-triac",
    category: "active",
    displayName: { tr: "Optokuplör TRIAC", en: "Optocoupler TRIAC" },
    symbol: "U",
    pins: [
        { id: "anode", side: "left", offset: -1, label: "A" },
        { id: "cathode", side: "left", offset: 1, label: "K" },
        { id: "mt1", side: "right", offset: -1, label: "MT1" },
        { id: "mt2", side: "right", offset: 1, label: "MT2" },
    ],
    parameterSchema: OptocouplerTriacParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=triac.js.map